import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote, Heart } from "lucide-react";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

export default function ReviewsSection() {
  const [data, setData] = useState({ reviews: [], total_reviews: 0, average_rating: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/reviews/featured?limit=6`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Don't render section if no reviews exist yet
  if (loading || !data.reviews?.length) return null;

  const stars = (n) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-4 h-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
      ))}
    </div>
  );

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-amber-50 via-white to-orange-50" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-full text-sm font-semibold mb-4">
            <Heart className="w-4 h-4 fill-white" />
            Trusted by J1 Students
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Real Reviews From Real Riders
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
            {stars(Math.round(data.average_rating))}
            <span className="text-2xl font-bold text-amber-600" data-testid="avg-rating">{data.average_rating.toFixed(1)}</span>
            <span className="text-muted-foreground text-sm">
              from <span className="font-semibold text-foreground" data-testid="total-reviews">{data.total_reviews}</span> verified review{data.total_reviews !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Review cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {data.reviews.map((r, idx) => (
            <Card
              key={idx}
              className="border-0 shadow-md hover:shadow-xl transition-all bg-white relative overflow-hidden"
              data-testid={`review-card-${idx}`}
            >
              <div className="absolute top-3 right-3 opacity-10">
                <Quote className="w-12 h-12 text-amber-500" />
              </div>
              <CardContent className="p-6">
                <div className="mb-3">{stars(r.rating)}</div>
                <p className="text-sm md:text-base text-foreground/90 leading-relaxed mb-4 italic">
                  "{r.comment}"
                </p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                    {(r.first_name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{r.first_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {r.trip_type === "return" ? "Return trip" : "Airport pickup"}
                      {r.pickup_location && ` • ${r.pickup_location}`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
